"use client";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  fetchBundleCards,
  fetchOrderBundles,
  putSelectedBundle,
  type OrderBundlesView,
} from "src/services/bundle-api";
import { useBundlesEnabled } from "src/utils/bundles-enabled";
import { orderQueryKey } from "src/hooks/useOrder";

export function useBundleCards(currency: string | null, locale: string) {
  const enabled = useBundlesEnabled();
  return useQuery({
    queryKey: ["bundles", "cards", currency, locale],
    queryFn: () => fetchBundleCards(currency ?? "eur", locale),
    enabled: enabled && !!currency,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** `amountKey` changes whenever the cart total does, so the view follows the cart. */
export function useOrderBundles(
  orderId: string | null | undefined,
  guestToken: string | null | undefined,
  amountKey: unknown,
  locale: string,
) {
  const enabled = useBundlesEnabled();
  return useQuery({
    queryKey: ["bundles", "order", orderId, amountKey, locale],
    queryFn: () => fetchOrderBundles(orderId as string, guestToken, locale),
    enabled: enabled && !!orderId,
    staleTime: 15_000,
    retry: false,
    // Keep this order's last view while the amount-keyed refetch runs, so the
    // hint does not blink out on every cart change. Never another order's.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === orderId ? previous : undefined,
  });
}

export function useSelectBundle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      orderId: string;
      guestToken: string | null | undefined;
      bundleId: string | null;
      locale: string;
    }) => putSelectedBundle(v.orderId, v.guestToken, v.bundleId, v.locale),
    onSuccess: (view: OrderBundlesView, v) => {
      queryClient.setQueriesData(
        { queryKey: ["bundles", "order", v.orderId] },
        view,
      );
      void queryClient.invalidateQueries({
        queryKey: orderQueryKey(v.orderId),
      });
    },
  });
}

const expiredKey = (orderId: string | null | undefined) =>
  ["bundles", "expired", orderId] as const;

/**
 * Keeps the `bundleExpired` note of a calculate-amount response (or any
 * order the server returns): the page refetches the order right after, and
 * that copy may not carry it. Merged with what is already noted.
 */
export function rememberBundleExpired(
  queryClient: QueryClient,
  orderId: string | null | undefined,
  response: unknown,
): void {
  if (!orderId) return;
  const list = (response as { bundleExpired?: unknown } | null | undefined)
    ?.bundleExpired;
  if (!Array.isArray(list) || list.length === 0) return;
  queryClient.setQueryData<unknown[]>(expiredKey(orderId), (prev) => [
    ...(prev ?? []),
    ...list,
  ]);
}

/** The noted `bundleExpired` entries for this order, as a source for expiredBundles. */
export function useRememberedBundleExpired(orderId: string | null | undefined) {
  const { data } = useQuery<unknown[]>({
    queryKey: expiredKey(orderId),
    queryFn: () => [],
    enabled: false,
    staleTime: Infinity,
  });
  return { bundleExpired: data ?? [] };
}
